"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { User } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { useEffect, useId, useRef } from "react";
import { FiInstagram, FiMail, FiMapPin } from "react-icons/fi";
import { LogoutButton } from "@/components/auth/logout-button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/hooks/use-auth";
import { useAuthEnabled, useCartEnabled } from "@/hooks/use-feature-flags";
import {
  lockBodyScroll,
  unlockBodyScroll,
  useFocusTrap,
  useLenisControl,
} from "@/hooks/use-header";
import { buildAccountUrl, routes } from "@/utils/routes";

// Premium animation variants for award-winning mobile menu experience
const menuVariants = {
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1] as const,
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const backdropVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
};

const contentVariants = {
  hidden: {
    x: "-100%",
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
  visible: {
    x: "0%",
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1] as const,
      // Delay accordion animations until slide-in completes
      staggerChildren: 0.15,
      delayChildren: 0.7, // Start accordion animations after slide-in
    },
  },
  // Exit variant: slower slide-out to make closing feel smoother
  exit: {
    x: "-100%",
    transition: {
      duration: 0.8, // slower than entrance
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
};

const sectionVariants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1] as const,
      // Stagger accordion title animations
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

// New variants for accordion title animations (Phase 2)
const accordionTitleVariants = {
  hidden: {
    opacity: 0,
    y: 20, // Slide in from bottom
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1] as const, // Premium spring-like easing
    },
  },
};

export default function MobileMenu({
  memoizedCategories,
  SUPPORT_LINKS,
  isMenuOpen,
  setIsMenuOpen,
}: {
  memoizedCategories: Array<{
    category: string;
    url: string;
    displayName: string;
    description: string;
  }>;
  SUPPORT_LINKS: { title: string; href: string }[];
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
}) {
  const { isAuthenticated, isLoading, fullName } = useAuth();
  const isAuthEnabled = useAuthEnabled();
  const isCartEnabled = useCartEnabled();
  const menuRef = useRef<HTMLDivElement>(null);
  const generatedId = useId().replace(/:/g, "");
  const titleId = `mobile-menu-title-${generatedId}`;
  const descriptionId = `mobile-menu-description-${generatedId}`;
  const { stop: stopLenis, start: startLenis } = useLenisControl();

  useFocusTrap(menuRef, isMenuOpen);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      lockBodyScroll();
      stopLenis();
      document.addEventListener("keydown", handleKeyDown);
    } else {
      unlockBodyScroll();
      startLenis();
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      unlockBodyScroll();
      startLenis(); // Ensure scrolling is re-enabled
    };
  }, [isMenuOpen, setIsMenuOpen, startLenis, stopLenis]);

  // Respect user preference for reduced motion
  const shouldReduceMotion = useReducedMotion();

  // Helper function to close menu when navigation links are clicked
  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <AnimatePresence mode="wait">
      {isMenuOpen && (
        <motion.div
          animate={shouldReduceMotion ? undefined : "visible"}
          aria-describedby={descriptionId}
          aria-labelledby={titleId}
          aria-modal="true"
          className="fixed right-0 left-0 z-40 lg:hidden"
          exit={shouldReduceMotion ? undefined : "hidden"}
          initial={shouldReduceMotion ? undefined : "hidden"}
          ref={menuRef}
          role="dialog"
          style={{
            top: "var(--site-header-height, 96px)",
            height: "calc(100dvh - var(--site-header-height, 96px))",
          }}
          tabIndex={-1}
          variants={shouldReduceMotion ? undefined : menuVariants}
        >
          {/* Visually hidden title and description for screen readers */}
          <h2 className="sr-only" id={titleId}>
            Main Menu
          </h2>
          <p className="sr-only" id={descriptionId}>
            Navigate through shop categories, quick links, and your account.
          </p>
          {/* Backdrop with blur effect */}
          <motion.div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)} // Close on overlay click
            variants={shouldReduceMotion ? undefined : backdropVariants}
          />

          {/* Menu content container - full width */}
          <motion.div
            className="relative h-full w-full border-border border-r bg-white shadow-xl"
            exit={shouldReduceMotion ? undefined : "exit"}
            style={{ willChange: "transform" }}
            variants={shouldReduceMotion ? undefined : contentVariants}
          >
            <div className="flex h-full flex-col bg-white">
              {/* Main navigation content - scrollable */}
              <nav
                className="flex-1 overflow-y-auto px-6 py-8"
                style={{
                  maxHeight:
                    "calc(100vh - var(--site-header-height, 96px) - 120px)", // Reserve space for footer
                }}
              >
                <motion.div
                  variants={shouldReduceMotion ? undefined : sectionVariants}
                >
                  <Accordion className="w-full" type="multiple">
                    {/* Shop Categories */}
                    <AccordionItem value="shop-categories">
                      <AccordionTrigger className="font-heading font-semibold text-foreground text-lg uppercase tracking-wider focus:ring-0 focus:ring-offset-0">
                        <motion.span
                          variants={
                            shouldReduceMotion
                              ? undefined
                              : accordionTitleVariants
                          }
                        >
                          Shop Categories
                        </motion.span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2">
                          {memoizedCategories.map(
                            ({ category, url, displayName, description }) => (
                              <Link
                                className="flex min-h-[48px] items-center rounded-lg bg-card p-4 transition-all duration-200 hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98]"
                                href={url as Route}
                                key={category}
                                onClick={handleLinkClick}
                              >
                                <div className="flex-1">
                                  <div className="font-heading font-medium text-base text-foreground">
                                    {displayName}
                                  </div>
                                  <div className="mt-1 text-muted-foreground text-sm leading-relaxed">
                                    {description}
                                  </div>
                                </div>
                              </Link>
                            )
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Quick Links */}
                    <AccordionItem value="quick-links">
                      <AccordionTrigger className="font-heading font-semibold text-foreground text-lg uppercase tracking-wider focus:ring-0 focus:ring-offset-0">
                        <motion.span
                          variants={
                            shouldReduceMotion
                              ? undefined
                              : accordionTitleVariants
                          }
                        >
                          Quick Links
                        </motion.span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2">
                          <Link
                            className="flex min-h-[48px] items-center rounded-lg bg-card px-4 py-3 font-heading font-medium text-base text-foreground transition-all duration-200 hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98]"
                            href={routes.shop as Route}
                            onClick={handleLinkClick}
                          >
                            All Products
                          </Link>
                          {SUPPORT_LINKS.map((link) => (
                            <Link
                              className="flex min-h-[48px] items-center rounded-lg bg-card px-4 py-3 font-heading font-medium text-base text-foreground transition-all duration-200 hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98]"
                              href={link.href as Route}
                              key={link.title}
                              onClick={handleLinkClick}
                            >
                              {link.title}
                            </Link>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Authentication Section - Only show when commerce is enabled */}
                    {isAuthEnabled && (
                      <AccordionItem value="account">
                        <AccordionTrigger className="font-heading font-semibold text-foreground text-lg uppercase tracking-wider focus:ring-0 focus:ring-offset-0">
                          <motion.span
                            variants={
                              shouldReduceMotion
                                ? undefined
                                : accordionTitleVariants
                            }
                          >
                            {isLoading
                              ? "Loading..."
                              : isAuthenticated
                                ? "My Account"
                                : "Account"}
                          </motion.span>
                        </AccordionTrigger>
                        <AccordionContent>
                          {isLoading && (
                            <div className="flex items-center justify-center py-4">
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                            </div>
                          )}

                          {!isLoading && isAuthenticated && (
                            <div className="space-y-2">
                              {fullName && (
                                <div className="px-4 py-3 font-medium text-foreground text-sm">
                                  <User className="mr-2 inline h-4 w-4" />
                                  {fullName}
                                </div>
                              )}
                              <Link
                                className="flex min-h-[48px] items-center rounded-lg bg-card px-4 py-3 font-heading font-medium text-base text-foreground transition-all duration-200 hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98]"
                                href={buildAccountUrl() as Route}
                                onClick={handleLinkClick}
                              >
                                My Account
                              </Link>
                              <Link
                                className="flex min-h-[48px] items-center rounded-lg bg-card px-4 py-3 font-heading font-medium text-base text-foreground transition-all duration-200 hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98]"
                                href={buildAccountUrl("orders") as Route}
                                onClick={handleLinkClick}
                              >
                                Order History
                              </Link>
                              {isCartEnabled && (
                                <Link
                                  className="flex min-h-[48px] items-center rounded-lg bg-card px-4 py-3 font-heading font-medium text-base text-foreground transition-all duration-200 hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98]"
                                  href={buildAccountUrl("wishlist") as Route}
                                  onClick={handleLinkClick}
                                >
                                  Wishlist
                                </Link>
                              )}
                            </div>
                          )}

                          {!(isLoading || isAuthenticated) && (
                            <div className="space-y-2">
                              <Link
                                className="flex min-h-[48px] items-center rounded-lg bg-card px-4 py-3 font-heading font-medium text-base text-foreground transition-all duration-200 hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98]"
                                href={routes.auth.login as Route}
                                onClick={handleLinkClick}
                              >
                                Sign In
                              </Link>
                              <Link
                                className="flex min-h-[48px] items-center rounded-lg bg-card px-4 py-3 font-heading font-medium text-base text-foreground transition-all duration-200 hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98]"
                                href={routes.auth.register as Route}
                                onClick={handleLinkClick}
                              >
                                Create Account
                              </Link>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    )}
                  </Accordion>
                </motion.div>
              </nav>

              {/* Footer Section - Fixed at bottom. Social links are always visible; Sign Out shown only when authenticated */}
              <motion.div
                className="border-border border-t bg-white px-6 py-4"
                variants={shouldReduceMotion ? undefined : sectionVariants}
              >
                {/* Sign Out Button (only visible to authenticated users when auth is enabled) */}
                {isAuthEnabled && !isLoading && isAuthenticated && (
                  <div className="mb-4">
                    <LogoutButton
                      className="flex min-h-[48px] w-full items-center rounded-lg bg-card px-4 py-3 font-heading font-medium text-base text-foreground transition-all duration-200 hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.98]"
                      variant="ghost"
                    >
                      Sign Out
                    </LogoutButton>
                  </div>
                )}

                {/* Social Media Links - always visible */}
                <div className="flex items-center justify-center gap-6">
                  <a
                    aria-label="Follow us on Instagram"
                    className="flex h-10 w-10 items-center justify-center rounded-lg bg-card text-muted-foreground transition-all duration-200 hover:bg-accent hover:text-primary hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.95]"
                    href="https://www.instagram.com/willpowerwellbeing/?hl=en"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <FiInstagram className="h-4 w-4" />
                  </a>
                  <a
                    aria-label="Send us an email"
                    className="flex h-10 w-10 items-center justify-center rounded-lg bg-card text-muted-foreground transition-all duration-200 hover:bg-accent hover:text-primary hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.95]"
                    href="mailto:willpowerwellbeing@hotmail.com"
                  >
                    <FiMail className="h-4 w-4" />
                  </a>
                  <a
                    aria-label="Find us on Google Maps"
                    className="flex h-10 w-10 items-center justify-center rounded-lg bg-card text-muted-foreground transition-all duration-200 hover:bg-accent hover:text-primary hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-[0.95]"
                    href="https://www.google.com/maps/search/?api=1&query=13+Great+Western+Arcade+Birmingham+UK+B32QD"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <FiMapPin className="h-4 w-4" />
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
